import type { ChildState } from "../api/types.ts";
import { copy } from "../copy/index.ts";
import { Button, Icon } from "./ui.tsx";
import styles from "./PracticeCards.module.css";

const t = copy.practiceHub;

/** T2 … CN; finished days get a honey check, today gets a ring (SPEC 6.7). */
export function WeekStrip({ daily, today }: { daily: ChildState["daily"]; today: string }) {
  return (
    <ol className={styles.week} aria-label={t.daily.title}>
      {daily.week.map((w, i) => (
        <li
          key={w.day}
          className={`${styles.day} ${w.done ? styles.dayDone : ""} ${w.day === today ? styles.today : ""} ${w.day > today ? styles.future : ""}`}
          aria-label={`${t.week[i]}${w.done ? t.weekDone : ""}`}
        >
          <span className={styles.dot} aria-hidden="true">
            {w.done ? <Icon name="check" size={22} /> : ""}
          </span>
          <span className={styles.dayName}>{t.week[i]}</span>
        </li>
      ))}
    </ol>
  );
}

export function DailyCard({
  state,
  onStart,
  onLearn,
}: {
  state: ChildState;
  onStart: () => void;
  onLearn: () => void;
}) {
  const { daily, today } = state;
  return (
    <section className={`${styles.card} ${styles.daily}`}>
      <header className={styles.head}>
        <Icon name="calendar" size={48} />
        <div>
          <h2>{t.daily.title}</h2>
          <p>{daily.unlocked ? t.daily.text : t.daily.locked}</p>
        </div>
      </header>
      <WeekStrip daily={daily} today={today} />
      {!daily.unlocked ? (
        <Button variant="secondary" block onClick={onLearn}>
          {t.daily.goLearn} ›
        </Button>
      ) : daily.doneToday ? (
        <>
          <p className={styles.done}>
            <Icon name="party_popper" size={24} /> {t.daily.doneToday}
          </p>
          <Button variant="secondary" block onClick={onStart}>
            {t.daily.more}
          </Button>
        </>
      ) : (
        <Button block onClick={onStart}>
          {t.daily.start} ›
        </Button>
      )}
    </section>
  );
}

/** Phone home screen: two big tappable tiles instead of the full cards. */
export function QuickTiles({
  state,
  onDaily,
  onReview,
  onLearn,
}: {
  state: ChildState;
  onDaily: () => void;
  onReview: () => void;
  onLearn: () => void;
}) {
  const { daily, review } = state;
  const n = review.dueCount;
  return (
    <div className={styles.tiles}>
      <button
        type="button"
        className={`${styles.tile} ${styles.daily}`}
        onClick={daily.unlocked ? onDaily : onLearn}
      >
        <Icon name="calendar" size={40} />
        <strong>{t.daily.title}</strong>
        <span className={styles.tileStatus}>
          {!daily.unlocked ? (
            <>
              <Icon name="lock" size={16} /> {t.daily.goLearn}
            </>
          ) : daily.doneToday ? (
            <span className={styles.ok}>
              <Icon name="check" size={16} /> {t.daily.doneShort}
            </span>
          ) : (
            `${t.daily.start} ›`
          )}
        </span>
      </button>
      <button
        type="button"
        className={`${styles.tile} ${styles.review}`}
        onClick={onReview}
        disabled={n === 0}
      >
        <Icon name="books" size={40} />
        <strong>{t.review.title}</strong>
        <span className={styles.tileStatus}>
          {n > 0 ? (
            t.review.due(n)
          ) : !daily.unlocked ? (
            t.review.notYetShort
          ) : (
            <span className={styles.ok}>
              <Icon name="check" size={16} /> {t.review.noneShort}
            </span>
          )}
        </span>
        {n > 0 && (
          <span className={`${styles.count} ${styles.tileCount}`} aria-hidden="true">
            {n}
          </span>
        )}
      </button>
    </div>
  );
}

export function ReviewCard({ state, onStart }: { state: ChildState; onStart: () => void }) {
  const n = state.review.dueCount;
  return (
    <section className={`${styles.card} ${styles.review}`}>
      <header className={styles.head}>
        <Icon name="books" size={48} />
        <div>
          <h2>{t.review.title}</h2>
          <p>{n > 0 ? t.review.text : state.daily.unlocked ? t.review.none : t.review.notYet}</p>
        </div>
        {n > 0 && (
          <span className={styles.count} aria-label={t.review.due(n)}>
            {n}
          </span>
        )}
      </header>
      {n > 0 && (
        <Button variant="success" block onClick={onStart}>
          {t.review.start} · {t.review.due(n)}
        </Button>
      )}
    </section>
  );
}
